import { useState } from 'react'
import { Button, DataTable, Icon, IconButton, Money, confirmDialog, type ColumnDef } from '../../components'
import { useDeleteRecurring, useMaterializeNow, useRecurring, useUpdateRecurring } from '../../lib/api/recurring'
import type { RecurringTemplate } from '../../lib/api/recurring'
import { humanizeRule } from './rule-builder'
import { RecurringDrawer } from './recurring-drawer'
import { useMe } from '../../lib/use-me'
import { canDelete } from '../../lib/permissions'

const TYPE_LABEL: Record<string, string> = {
  income: 'Прихід',
  expense: 'Витрата',
  transfer: 'Переказ',
}

export function RecurringTab() {
  const me = useMe()
  const allowDelete = canDelete(me.data?.role)

  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('')
  const [drawerItem, setDrawerItem] = useState<RecurringTemplate | null | undefined>(undefined)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const list = useRecurring({
    isActive: filterActive === 'true' ? true : filterActive === 'false' ? false : undefined,
  })
  const deleteRecurring = useDeleteRecurring()
  const updateRecurring = useUpdateRecurring()
  const materialize = useMaterializeNow()

  const items = list.data?.items ?? []

  const openCreate = () => {
    setDrawerItem(null)
    setDrawerOpen(true)
  }

  const openEdit = (item: RecurringTemplate) => {
    setDrawerItem(item)
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setDrawerItem(undefined)
  }

  const toggleActive = (item: RecurringTemplate) => {
    updateRecurring.mutate({ id: item.id, isActive: !item.isActive })
  }

  const columns: ColumnDef<RecurringTemplate>[] = [
    {
      id: 'name',
      header: 'Назва',
      sortValue: (item) => item.name,
      cell: (item) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="repeat" size={12} color="var(--accent)" />
          {item.name}
        </span>
      ),
    },
    {
      id: 'type',
      header: 'Тип',
      width: 90,
      cellClassName: 'fg3',
      sortValue: (item) => item.type,
      cell: (item) => TYPE_LABEL[item.type],
    },
    {
      id: 'rule',
      header: 'Правило',
      sortValue: (item) => item.rule,
      cell: (item) => (
        <span
          style={{ fontSize: 12, color: 'var(--fg-2)', fontFamily: 'var(--font-mono)' }}
          title={item.rule}
        >
          {humanizeRule(item.rule)}
        </span>
      ),
    },
    {
      id: 'nextRun',
      header: 'Наступний запуск',
      width: 130,
      cellClassName: 'fg2',
      sortValue: (item) => item.nextRunDate,
      cell: (item) => (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{item.nextRunDate}</span>
      ),
    },
    {
      id: 'amount',
      header: 'Сума',
      width: 130,
      thClassName: 'num',
      cellClassName: 'num',
      sortValue: (item) => item.amount,
      cell: (item) => <Money amount={item.amount} currency={item.currency} />,
    },
    {
      id: 'active',
      header: 'Активний',
      width: 80,
      sortValue: (item) => (item.isActive ? 1 : 0),
      cell: (item) => (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={item.isActive} onChange={() => toggleActive(item)} />
          <span
            style={{ fontSize: 12, color: item.isActive ? 'var(--success-fg)' : 'var(--fg-4)' }}
          >
            {item.isActive ? 'Так' : 'Ні'}
          </span>
        </label>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 120,
      pinned: true,
      cell: (item) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <IconButton
            icon="zap"
            tooltip="Згенерувати зараз"
            onClick={async () => {
              const ok = await confirmDialog({
                title: 'Матеріалізувати шаблон?',
                body: 'Буде створено нову планову операцію на основі цього шаблону.',
                tone: 'default',
              })
              if (ok) materialize.mutate(item.id)
            }}
          />
          <IconButton icon="pencil" tooltip="Редагувати" onClick={() => openEdit(item)} />
          {allowDelete && (
            <IconButton
              icon="trash-2"
              tooltip="Видалити"
              onClick={async () => {
                const ok = await confirmDialog({
                  title: 'Видалити шаблон?',
                  body: 'Наявні планові операції не буде видалено.',
                  tone: 'danger',
                })
                if (ok) deleteRecurring.mutate(item.id)
              }}
            />
          )}
        </div>
      ),
    },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: 'var(--fg-3)' }}>Активність:</label>
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as typeof filterActive)}
            className="input"
            style={{ width: 140 }}
          >
            <option value="">Всі</option>
            <option value="true">Активні</option>
            <option value="false">Неактивні</option>
          </select>
        </div>
        <Button variant="primary" size="sm" icon="plus" onClick={openCreate}>
          Новий шаблон
        </Button>
      </div>

      {list.isLoading && <SkeletonRows />}

      {!list.isLoading && items.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ color: 'var(--fg-2)', marginBottom: 12 }}>
            Регулярних шаблонів не знайдено.
          </div>
          <Button variant="primary" size="sm" icon="plus" onClick={openCreate}>
            Додати перший
          </Button>
        </div>
      )}

      {!list.isLoading && items.length > 0 && (
        <div className="card">
          <DataTable
            tableKey="recurring"
            columns={columns}
            rows={items}
            rowKey={(item) => item.id}
          />
        </div>
      )}

      {drawerOpen && (
        <RecurringDrawer
          item={drawerItem ?? null}
          onClose={closeDrawer}
        />
      )}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div className="card" style={{ padding: 20 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 36,
            background: 'var(--bg-3)',
            borderRadius: 4,
            marginBottom: 6,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  )
}
